import {refreshUserToken} from './../../Users/ducks/userProfile';
import {AnyAction, Dispatch} from 'redux';
import {sequenceT} from 'fp-ts/lib/Apply';
import {log} from 'fp-ts/lib/Console';
import * as E from 'fp-ts/lib/Either';
import {flow} from 'fp-ts/lib/function';
import * as IO from 'fp-ts/lib/IO';
import * as IOE from 'fp-ts/lib/IOEither';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {getExpiresAtTime, logout} from 'Shared/utilities/Auth';
import {setTimeoutIO} from './IOUtilities';

const logoutAndReturnToHomepage = sequenceT(IO.io)(log('Logging user out due to inactivity.'), logout);

export const startInactivityDetection = (onLogout: () => void, dispatch: Dispatch) => {
  const INACTIVITY_TIMEOUT_THRESHOLD = 15 * 60 * 1000;
  let timerId: O.Option<number> = O.none;

  const startInactivityTimer = () =>
    (setTimeout(
      sequenceT(IO.io)(logoutAndReturnToHomepage, onLogout),
      INACTIVITY_TIMEOUT_THRESHOLD
    ) as unknown) as number;

  const resetInactivityTimer = () => {
    O.option.ap(
      O.some((id: number) => window.clearTimeout(id)),
      timerId
    );
    timerId = O.some(startInactivityTimer());
  };

  document.addEventListener('mousemove', resetInactivityTimer, false);
  document.addEventListener('mousedown', resetInactivityTimer, false);
  document.addEventListener('keypress', resetInactivityTimer, false);
  document.addEventListener('touchmove', resetInactivityTimer, false);

  timerId = O.some(startInactivityTimer());
  window.onmessage = (e: any) => {
    if (e.data === 'timer') {
      startRefreshTimer(dispatch);
      const iFrames = Array.from(document.getElementsByTagName('iframe'));
      for (const iFrame of iFrames) {
        iFrame.remove();
      }
    }
  };
};

let currentTokenTimerId: O.Option<number> = O.none;

const getMillisecondsUntilRefresh = (currentTime: number) => (secondsBefore: number) => (expiresAtTime: number) =>
  expiresAtTime - currentTime - secondsBefore * 1000;

const dispatchAction = (dispatch: Dispatch, actionCreator: AnyAction) => dispatch(actionCreator);

const setTokenRefreshTimer = (currentTime: number) => (secondsBefore: number) => (dispatch: Dispatch) => {
  return pipe(
    getExpiresAtTime,
    IOE.map(
      flow(
        getMillisecondsUntilRefresh(currentTime)(secondsBefore),
        IO.of,
        IO.map(setTimeoutIO(() => dispatchAction(dispatch, refreshUserToken())))
      )
    )
  );
};

export const startRefreshTimer = (dispatch: Dispatch) => {
  O.option.ap(
    O.some((timerId: number) => window.clearTimeout(timerId)),
    currentTokenTimerId
  );
  const getCurrentTime = () => new Date().getTime();
  const getSecondsToRefreshBeforeTimeout = IO.io.of(30 * 60);

  const result = pipe(
    IOE.rightIO(() => setTokenRefreshTimer),
    IOE.ap(IOE.rightIO(getCurrentTime)),
    IOE.ap(IOE.rightIO(getSecondsToRefreshBeforeTimeout)),
    IOE.ap(IOE.right(dispatch)),
    IOE.bimap(log, setTimer => setTimer()),
    IOE.map(E.map(IO.map(IO.map(timerId => O.some((timerId as unknown) as number)))))
  )();

  if (E.isRight(result) && E.isRight(result.right)) {
    currentTokenTimerId = result.right.right()();
  }
};
